import { LineStringFeature, extrasWithSurfaces } from '../../utils/models/LineStringFeature';
import { edgeValues, rangesForLine } from '../../utils/models/segmentSurfaces';
import { PROFILE_LINE, cumulativeDistancesMeters, mergedInstructions } from '../../utils/models/turnInstructions';
import { seamSignsByJoin } from '../../utils/models/navigation/routeDirections';
import { generateID } from '../../utils/helpers/IDGenerator';
import { directionsLineStringsOf } from './turnInstructionsSource';

/**
 * A route's line strings `from` to `to` joined into one, for deleted control points: the turns
 * of every segment carried across each join, not just the first segment's.
 */
export function mergeLineStrings(
  route: { lineStrings: LineStringFeature[] },
  from: number,
  to: number
): LineStringFeature {
  const junctions = seamSignsByJoin(directionsLineStringsOf(route));
  const first = route.lineStrings[from];

  let coordinates = [...first.lineStringGeometry.coordinates];
  let instructions = first.lineStringProperties?.instructions ?? [];
  let surfaces = edgeValues(first.lineStringProperties?.extras?.surface?.values, coordinates.length);

  for (let index = from + 1; index <= to; index++) {
    const next = route.lineStrings[index];
    const nextCoordinates = next.lineStringGeometry.coordinates;
    instructions = mergedInstructions(
      instructions,
      lengthOf(coordinates),
      next.lineStringProperties?.instructions ?? [],
      lengthOf(nextCoordinates),
      junctions.get(index - 1)
    );
    // Both copies of the join can't stay, but one has to, or the line cuts the corner.
    coordinates = [...coordinates, ...nextCoordinates.slice(1)];
    surfaces = [...surfaces, ...edgeValues(next.lineStringProperties?.extras?.surface?.values, nextCoordinates.length)];
  }

  const merged = LineStringFeature.coordsOnlyFeature(coordinates);
  merged.lineStringProperties = {
    ...first.lineStringProperties,
    id: generateID(),
    instructions,
    extras: extrasWithSurfaces(first.lineStringProperties?.extras, rangesForLine(coordinates, surfaces)),
    profile: mergedProfile(route.lineStrings.slice(from, to + 1).map((ls) => ls.lineStringProperties?.profile))
  };
  return merged;
}

/** The first snapped profile, since the merge keeps its turns; straight only if every part was. */
function mergedProfile(profiles: (string | null | undefined)[]): string | null {
  const known = profiles.filter((profile): profile is string => !!profile);
  return known.find((profile) => profile !== PROFILE_LINE) ?? known[0] ?? null;
}

function lengthOf(coordinates: number[][]): number {
  return cumulativeDistancesMeters(coordinates)[coordinates.length - 1] ?? 0;
}
